import {calculateErrorMessage} from '.'

export interface WAVRecorderConfig {
  sampleRate?: number
  channels?: number
  bitDepth?: number
  echoCancellation?: boolean
  noiseSuppression?: boolean
  autoGainControl?: boolean
  /** Microphone to record from; empty/omitted = the system default. */
  deviceId?: string
}

export interface WAVRecorderCallbacks {
  onStart?: () => void
  onStop?: (blob: Blob) => void
  onError?: (error: Error) => void
  onDataAvailable?: (duration: number) => void
}

/** What the last `stop()` captured, measured before the samples were released. */
export interface WAVRecordingStats {
  durationMs: number
  /** Milliseconds of speech-like energy; see `measureActiveSpeechMs`. */
  activeSpeechMs: number
}

const SPEECH_FRAME_MS = 20
const SPEECH_ABSOLUTE_FLOOR_DBFS = -50
const SPEECH_ABOVE_NOISE_FLOOR_DB = 5
const SPEECH_NOISE_FLOOR_PERCENTILE = 0.1

/**
 * Milliseconds of speech in a recording, by frame energy.
 *
 * The same measure the server applies before transcribing (node-api
 * speech/audioActivity.js): 20ms frames, a frame counts when it is above -50 dBFS AND 5 dB above the
 * clip's own noise floor (its 10th-percentile frame). Steady noise never rises above itself, so a
 * silent or hissing recording measures ~0 however loud it is. Kept identical on both sides so the
 * browser does not upload a clip the server would reject, nor skip one it would accept.
 */
export function measureActiveSpeechMs(samples: Float32Array, sampleRate: number): number {
  const frameSize = Math.max(1, Math.round((sampleRate * SPEECH_FRAME_MS) / 1000))
  const frameCount = Math.floor(samples.length / frameSize)
  if (frameCount === 0) return 0

  const energies = new Array<number>(frameCount)
  for (let f = 0; f < frameCount; f++) {
    let sum = 0
    const start = f * frameSize
    for (let s = 0; s < frameSize; s++) {
      // Clamped exactly as floatTo16BitPCM clamps, so both sides measure the same signal.
      const v = Math.max(-1, Math.min(1, samples[start + s]))
      sum += v * v
    }
    const rms = Math.sqrt(sum / frameSize)
    energies[f] = rms > 0 ? 20 * Math.log10(rms) : -120
  }

  const sorted = [...energies].sort((a, b) => a - b)
  const noiseFloor =
    sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * SPEECH_NOISE_FLOOR_PERCENTILE))]
  const threshold = Math.max(SPEECH_ABSOLUTE_FLOOR_DBFS, noiseFloor + SPEECH_ABOVE_NOISE_FLOOR_DB)

  let active = 0
  for (const energy of energies) if (energy > threshold) active++
  return active * SPEECH_FRAME_MS
}

export class WAVRecorder {
  private config: Required<Omit<WAVRecorderConfig, 'deviceId'>>
  private deviceId: string
  private lastStats: WAVRecordingStats | null = null
  private callbacks: WAVRecorderCallbacks

  private audioContext: AudioContext | null = null
  private mediaStream: MediaStream | null = null
  private sourceNode: MediaStreamAudioSourceNode | null = null
  private processorNode: ScriptProcessorNode | null = null

  private cancelledDuringStart = false
  private audioChunks: Float32Array[] = []
  private isRecording = false
  private startTime = 0

  constructor(config: WAVRecorderConfig = {}, callbacks: WAVRecorderCallbacks = {}) {
    this.config = {
      sampleRate: config.sampleRate ?? 16000,
      channels: config.channels ?? 1,
      bitDepth: config.bitDepth ?? 16,
      echoCancellation: config.echoCancellation ?? true,
      noiseSuppression: config.noiseSuppression ?? true,
      autoGainControl: config.autoGainControl ?? true,
    }
    this.deviceId = config.deviceId ?? ''
    this.callbacks = callbacks
  }

  /**
   * Capability check only — it must never touch the microphone. Callers probe this on
   * mount to decide whether to show a mic button, and opening a stream here made the
   * browser's permission prompt appear as soon as the chat widget mounted instead of when
   * the user pressed the mic. Permission is requested by `start()`, whose caller maps a
   * denial to its own error state.
   */
  static async isSupported(): Promise<boolean> {
    if (typeof window === 'undefined') return false
    const hasAudioContext = !!(window.AudioContext || (window as any).webkitAudioContext)
    const hasGetUserMedia = !!navigator?.mediaDevices?.getUserMedia
    return hasAudioContext && hasGetUserMedia
  }

  async start(): Promise<void> {
    if (this.isRecording) {
      throw new Error('Already recording')
    }

    if (!(await WAVRecorder.isSupported())) {
      throw new Error('Audio recording is not supported in this browser')
    }

    this.cancelledDuringStart = false

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: this.config.channels,
          sampleRate: this.config.sampleRate,
          echoCancellation: this.config.echoCancellation,
          noiseSuppression: this.config.noiseSuppression,
          autoGainControl: this.config.autoGainControl,
          ...(this.deviceId ? {deviceId: {exact: this.deviceId}} : {}),
        },
      })

      try {
        const gw = window as any
        gw.__activeVoiceMediaStreams = gw.__activeVoiceMediaStreams || new Set()
        gw.__activeVoiceAudioContexts = gw.__activeVoiceAudioContexts || new Set()
        gw.__activeVoiceMediaStreams.add(this.mediaStream)
        if (this.audioContext) gw.__activeVoiceAudioContexts.add(this.audioContext)
      } catch (err) {
        calculateErrorMessage(err)
      }

      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
      this.audioContext = new AudioContextClass({
        sampleRate: this.config.sampleRate,
      })

      if (this.cancelledDuringStart) {
        try {
          this.mediaStream.getTracks().forEach((t) => t.stop())
        } catch (err) {
          calculateErrorMessage(err)
        }
        this.mediaStream = null
        if (this.audioContext && this.audioContext.state !== 'closed') {
          this.audioContext.close().catch(console.error)
        }
        this.audioContext = null
        return
      }

      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream)

      this.processorNode = this.audioContext.createScriptProcessor(
        4096,
        this.config.channels,
        this.config.channels
      )

      this.processorNode.onaudioprocess = (event: AudioProcessingEvent) => {
        if (!this.isRecording) return

        const inputData = event.inputBuffer.getChannelData(0)

        const audioData = new Float32Array(inputData.length)
        audioData.set(inputData)
        this.audioChunks.push(audioData)

        if (this.callbacks.onDataAvailable) {
          const duration = (Date.now() - this.startTime) / 1000
          this.callbacks.onDataAvailable(duration)
        }
      }

      this.sourceNode.connect(this.processorNode)
      this.processorNode.connect(this.audioContext.destination)

      this.isRecording = true
      this.startTime = Date.now()
      this.audioChunks = []

      this.callbacks.onStart?.()
    } catch (error) {
      this.cleanup()
      const err = error instanceof Error ? error : new Error(String(error))
      calculateErrorMessage(error)
      this.callbacks.onError?.(err)
      throw err
    }
  }

  async stop(): Promise<Blob> {
    if (!this.isRecording) {
      throw new Error('Not currently recording')
    }

    this.isRecording = false

    try {
      const wavBlob = this.createWAVBlob()

      this.cleanup()

      this.callbacks.onStop?.(wavBlob)

      return wavBlob
    } catch (error) {
      this.cleanup()
      const err = error instanceof Error ? error : new Error(String(error))
      calculateErrorMessage(error)
      this.callbacks.onError?.(err)
      throw err
    }
  }

  cancel(): void {
    this.isRecording = false
    this.cancelledDuringStart = true
    this.cleanup()
  }

  /** Duration and active speech of the recording the last `stop()` returned, or null. */
  getLastRecordingStats(): WAVRecordingStats | null {
    return this.lastStats
  }

  getState(): {
    isRecording: boolean
    duration: number
    chunks: number
  } {
    return {
      isRecording: this.isRecording,
      duration: this.isRecording ? (Date.now() - this.startTime) / 1000 : 0,
      chunks: this.audioChunks.length,
    }
  }

  private createWAVBlob(): Blob {
    const totalLength = this.audioChunks.reduce((sum, chunk) => sum + chunk.length, 0)
    const mergedSamples = new Float32Array(totalLength)

    let offset = 0
    for (const chunk of this.audioChunks) {
      mergedSamples.set(chunk, offset)
      offset += chunk.length
    }

    // The rate the context actually ran at: a browser may ignore the requested one.
    const actualRate = this.audioContext?.sampleRate || this.config.sampleRate
    this.lastStats = {
      durationMs: Math.round((mergedSamples.length / actualRate) * 1000),
      activeSpeechMs: measureActiveSpeechMs(mergedSamples, actualRate),
    }

    const pcmData = this.floatTo16BitPCM(mergedSamples)

    const wavBuffer = this.createWAVBuffer(pcmData)

    return new Blob([wavBuffer], {type: 'audio/wav'})
  }

  private floatTo16BitPCM(float32Array: Float32Array): Int16Array {
    const int16Array = new Int16Array(float32Array.length)

    for (let i = 0; i < float32Array.length; i++) {
      const sample = Math.max(-1, Math.min(1, float32Array[i]))
      int16Array[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff
    }

    return int16Array
  }

  private createWAVBuffer(pcmData: Int16Array): ArrayBuffer {
    const {sampleRate, channels, bitDepth} = this.config
    const bytesPerSample = bitDepth / 8
    const blockAlign = channels * bytesPerSample
    const byteRate = sampleRate * blockAlign
    const dataSize = pcmData.length * bytesPerSample

    const buffer = new ArrayBuffer(44 + dataSize)
    const view = new DataView(buffer)

    this.writeString(view, 0, 'RIFF')
    view.setUint32(4, 36 + dataSize, true)
    this.writeString(view, 8, 'WAVE')

    this.writeString(view, 12, 'fmt ')
    view.setUint32(16, 16, true)
    view.setUint16(20, 1, true)
    view.setUint16(22, channels, true)
    view.setUint32(24, sampleRate, true)
    view.setUint32(28, byteRate, true)
    view.setUint16(32, blockAlign, true)
    view.setUint16(34, bitDepth, true)

    this.writeString(view, 36, 'data')
    view.setUint32(40, dataSize, true)

    let offset = 44
    for (let i = 0; i < pcmData.length; i++, offset += 2) {
      view.setInt16(offset, pcmData[i], true)
    }

    return buffer
  }

  private writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i))
    }
  }

  private cleanup(): void {
    try {
      if (this.processorNode) {
        this.processorNode.disconnect()
        this.processorNode.onaudioprocess = null
        this.processorNode = null
      }

      if (this.sourceNode) {
        try {
          this.sourceNode.disconnect()
        } catch (err) {
          calculateErrorMessage(err)
        }
        this.sourceNode = null
      }

      try {
        const gw = window as any
        if (this.audioContext) {
          try {
            if (this.audioContext.state !== 'closed') this.audioContext.close().catch(console.error)
          } catch (err) {
            calculateErrorMessage(err)
          }
          try {
            gw.__activeVoiceAudioContexts?.delete(this.audioContext)
          } catch (err) {
            calculateErrorMessage(err)
          }
        }
      } catch (err) {
        calculateErrorMessage(err)
      }

      if (this.audioContext) this.audioContext = null

      if (this.mediaStream) {
        try {
          this.mediaStream.getTracks().forEach((track) => track.stop())
        } catch (err) {
          calculateErrorMessage(err)
        } finally {
          try {
            const gw = window as any
            gw.__activeVoiceMediaStreams?.delete(this.mediaStream)
          } catch (err) {
            calculateErrorMessage(err)
          }
          this.mediaStream = null
        }
      }

      this.audioChunks = []
    } catch (error) {
      calculateErrorMessage(error)
    }
  }
}

export default WAVRecorder
